import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { Button, Grid, Box, autocompleteClasses } from '@mui/material';
import 'react-tabulator/lib/styles.css'; // Default theme
import 'react-tabulator/css/bootstrap/tabulator_bootstrap.min.css'; // Bootstrap theme
import { ReactTabulator } from 'react-tabulator';
import TitleText from '../../components/formlib/TitleText';
import './reports.css';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs, { Dayjs } from 'dayjs';
import { formatName } from '../../utils/nameUtils';
import EmptyScreen from '../../components/shared/EmptyScreen';
import {
  CancelationReportAppointments,
  CancelationReportJSON,
  downloadCancelationReport,
  getCancelationReport,
} from '../../services/reportsApi/report';
import AppPagination from '../../components/shared/AppPagination';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import { ApiError, isCustomError } from '../../services/ApiResponseHandler';
import { Client, OptionType, ResponseType } from '../../utils/type';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import DisplayFilter, { FilterBy } from '../../components/shared/DisplayFilter';
import {
  Employee,
  fetchActiveEmployees,
} from '../../services/configApi/employees/employeeServices';
import { Formik, FormikProps } from 'formik';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ReactComponent as Filtericon } from '../../assets/images/FilterIconsvg.svg';
import { right } from '@popperjs/core';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import DatePickerComponent from '../../components/formlib/DatePickerComponent';
import MediumTypography from '../../components/formlib/MediumTypography';
import MultiSelectAutocompleteSearch, {
  MultiSelectOptioinType,
} from '../../components/formlib/MultiSelectAutocompleteSearch';
import * as yup from 'yup';
import { appointmentTypesFilter } from '../scheduler/schedulerUtils';
import MultipleAutocompleteSearch from '../../components/formlib/MultipleAutocompleteSearch';
import { getAllClients } from '../../services/configApi/Clients';
import { encodeURI } from 'js-base64';
import { checkOffline } from '../../services/Offline';
import { datePickerMinDateMaxDateValidate } from '../../utils/dateUtil';
import { debounce } from 'lodash';
import CustomMenu from '../../components/formlib/CustomMenu';

// Define table columns
const columns = [
  {
    title: 'Appointment Type',
    field: 'appointmentType',
    minWidth: 160,
    maxWidth: 160,
    tooltip: true,
  },
  {
    title: 'Attendees',
    field: 'attendee',
    formatter: 'html',
    minWidth: 150,
    maxWidth: 150,
    tooltip: true,
  },
  {
    title: 'Client Name',
    field: 'clientName',
    minWidth: 150,
    maxWidth: 150,
    tooltip: true,
  },
  {
    title: 'Enrollment ID',
    field: 'dphId',
    minWidth: 150,
    maxWidth: 150,
    tooltip: false,
  },
  {
    title: 'Status',
    field: 'progressNotesStatus',
    formatter: 'html',
    minWidth: 100,
    maxWidth: 100,
  },
  {
    title: 'Canceled Type',
    field: 'canceledType',
    minWidth: 160,
    maxWidth: 160,
    tooltip: true,
  },
  {
    title: 'Canceled Reason',
    field: 'canceledReason',
    minWidth: 100,
    maxWidth: 250,
    widthGrow: 2,
    tooltip: true,
    clipContents: true,
  },
];

interface RawData {
  clientName: string;
  date: string;
  appointmentType: string;
  attendee: string;
  canceledType: string;
  canceledReason: string;
  progressNotesStatus: string;
}

export type CancelationReportFilterType = {
  startDate: Dayjs | null | string;
  endDate: Dayjs | null | string;
  appointmentType: OptionType[];
  canceledType: string | null;
  attendeeIds: Employee[];
  clientIds: Client[];
};

const validationSchema = yup.object().shape({
  startDate: yup
    .string()
    .nullable()
    .test('required-if-endDate', 'IFSP.startDateRequired', function (value) {
      const { endDate } = this.parent;
      if (endDate && !value) {
        return false;
      }
      return true;
    }),
  endDate: yup
    .string()
    .nullable()
    .test('required-if-startDate', 'IFSP.endDateRequired', function (value) {
      const { startDate } = this.parent;
      if (startDate && !value) {
        return false;
      }
      return true;
    }),
  appointmentType: yup.array().optional(),
  // canceledType: yup.string().optional(),
  attendeeIds: yup.array().optional(),
  clientIds: yup.array().optional(),
});

const CancelationReport = () => {
  const intl = useIntl();
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const [reportData, setReportData] = useState<CancelationReportJSON>({
    totalCount: 0,
    cancellationReportAppointments: [],
  });
  const [page, setPage] = useState<number>(0);
  const itemPerPage = 10;
  const [successOrError, setSuccessOrError] = useState<ResponseType>('success');
  const [toastrId, setToastrId] = useState<string>();
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [filterBy, setFilterBy] = React.useState<FilterBy[]>([]);
  const isInitialMount = React.useRef(true);
  const [filterPlainData, setFilterPlainData] =
    React.useState<CancelationReportFilterType>({
      startDate: null,
      endDate: null,
      appointmentType: [],
      canceledType: null,
      clientIds: [],
      attendeeIds: [],
    });

  const formRef = React.useRef<FormikProps<CancelationReportFilterType>>(null);
  const [filterData, setFilterData] = React.useState('');
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const MemorizedCustomMenu = useMemo(() => CustomMenu, []);
  const openit = Boolean(anchorEl);
  const [clientSearchText, setClientSearchText] = useState<string>('');
  const [employeeList, setEmployeeList] = useState<Employee[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [offlineMode, setOfflineMode] = useState(false);

  useLayoutEffect(() => {
    const getOfflineMode = async () => {
      const offlineFlag = await checkOffline();
      setOfflineMode(offlineFlag);
    };
    getOfflineMode();
  });

  useEffect(() => {
    getReportList();
  }, []);

  useEffect(() => {
    setReportData({
      totalCount: reportData.totalCount,
      cancellationReportAppointments: [],
    });
    if (!isInitialMount.current) {
      getReportList();
    }
  }, [page, filterData]);

  const handleClientSearchChange = useCallback(
    debounce((value: string) => {
      getAllClients(false, value)
        .then((response) => {
          setClients(response);
          toggleLoader(false);
        })
        .catch(() => {
          toggleLoader(false);
        });
    }, 500),
    [],
  );

  const getEmployees = () => {
    fetchActiveEmployees()
      .then((data) => {
        let employeelist: Employee[] = [];
        employeelist = data.map((employee) => {
          return createEmployeeObj(
            employee.id,
            employee.name,
            employee.email,
            employee.firstName,
            employee.lastName,
            employee.disciplineType,
            employee.phone,
          );
        });

        setEmployeeList([...employeelist]);
        handleClientSearchChange(clientSearchText);
      })
      .catch((error) => {
        toggleLoader(false);
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
          setSuccessOrError('error');
        } else {
          setToastrId('failedToGetEmployee');
          setToastrDefaultMessage('Failed to get employee data');
          setSuccessOrError('error');
        }
      });
  };

  const createEmployeeObj = (
    id: string,
    name: string | null,
    email: string | null,
    firstName: string | null,
    lastName: string | null,
    disciplineType: string | null,
    phone: string | null,
  ) => {
    return {
      id: id,
      name: name,
      firstName: firstName,
      lastName: lastName,
      email: email,
      disciplineType: disciplineType,

      nameSuffix: '',
      clientId: '',
      kcUsername: '',
      middleName: '',
      preferredName: '',
      phone1: phone,
      phone2: '',
      workingHoursPerWeek: '',
      productivityGoal: null,
      allowProductivityGoalEdit: null,
      employeeRole: '',
      reportingTo: '',
      employeeType: '',
      startDate: '',
      endDate: '',
      status: '',
      primaryAddress1: '',
      primaryAddress2: '',
      primaryZipcode: '',
      primaryCity: '',
      primaryState: '',
      secondaryAddressRequired: false,
      secondaryAddress1: '',
      secondaryAddress2: '',
      secondaryZipcode: '',
      secondaryCity: '',
      secondaryState: '',
      emergencyFirstName: '',
      emergencyLastName: '',
      emergencyPhone: '',
      emergencyEmail: '',
      emergencyAddress1: '',
      emergencyAddress2: '',
      emergencyZipcode: '',
      emergencyCity: '',
      emergencyState: '',
      programs: [],
      modifiedOn: '',
      allowDelete: null,
      npi: null,
      employeeCredentials: null,
      signature: '',
      pin: '',
      phoneNumberExtension: '',
      phoneNumberExtension2: '',
      defaultProgram: '',
      superProgramId: '',
      lunchWaiverRequired: false,
      lunchWaiverStartDate: null,
      group: {
        id: '',
        groupName: '',
      },
    };
  };

  const clearForm = () => {
    setPage(0);
    formRef.current?.resetForm();
    setFilterPlainData({
      startDate: null,
      endDate: null,
      appointmentType: [],
      canceledType: null,
      clientIds: [],
      attendeeIds: [],
    });
    setFilterBy([]);
    setFilterData('');
    setAnchorEl(null);
  };

  const handlefilter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handlefilterClose = () => {
    setAnchorEl(null);
  };

  const applyFilter = (values: CancelationReportFilterType) => {
    const filterDataBy: FilterBy[] = [];
    setFilterPlainData(values);

    if (
      values.endDate != null &&
      values.endDate !== '' &&
      values.startDate != null &&
      values.startDate !== ''
    ) {
      filterDataBy.push({
        key: 'dateText',
        value: values.startDate + ' - ' + values.endDate,
      });
    }
    if (values.clientIds.length > 0) {
      const clientsFilter: OptionType[] = values.clientIds.map((client) => {
        const specificClient: OptionType = {
          id: client.id.toString(),
          label: formatName(
            client.firstName,
            client.middleName,
            client.lastName,
            client.suffix,
          ),
        };
        return specificClient;
      });

      filterDataBy.push({
        key: 'TaskManager.clientfilter',
        value: clientsFilter,
      });
    }
    if (values.attendeeIds.length > 0) {
      const attendeesFilter: OptionType[] = values.attendeeIds.map((emp) => {
        const specificClient: OptionType = {
          id: emp.id,
          label: emp.firstName + ' ' + emp.lastName,
        };
        return specificClient;
      });
      filterDataBy.push({
        key: 'ViewSchedulerEvent.clinicians',
        value: attendeesFilter,
      });
    }

    if (values.appointmentType.length > 0) {
      const appointments: OptionType[] = values.appointmentType.map(
        (appt: OptionType) => {
          const specificClient: OptionType = {
            id: appt.id,
            label: appt.label,
          };
          return specificClient;
        },
      );
      filterDataBy.push({
        key: 'AddNewSchedulerEvent.appointmentType',
        value: appointments,
      });
    }
    setFilterBy(filterDataBy);

    if (filterDataBy.length <= 2) {
      if (filterDataBy.some((item) => item.value === 'false')) {
        clearForm();
        setFilterBy([]);
        configureFilterAndCallAPI(null);
      } else {
        configureFilterAndCallAPI(values);
      }
    } else {
      configureFilterAndCallAPI(values);
    }
  };

  const getReportList = async () => {
    toggleLoader(true);
    await getCancelationReport(page, itemPerPage, filterData)
      .then((response) => {
        isInitialMount.current = false;
        setReportData(response);
        if (clients.length === 0 || employeeList.length === 0) {
          getEmployees();
        } else {
          toggleLoader(false);
        }
      })
      .catch((error) => {
        isInitialMount.current = false;
        toggleLoader(false);
        setSuccessOrError('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('cancelationReportGetFailureMessage');
          setToastrDefaultMessage('Failed to get cancellation report details');
        }
      });
  };

  const configureFilterAndCallAPI = (
    values: CancelationReportFilterType | null,
  ) => {
    let filterString: string = '';

    if (values?.endDate && values?.startDate) {
      filterString = filterString
        .concat('filterByAppointmentDate>=')
        .concat(values.startDate.toString())
        .concat('&')
        .concat('filterByAppointmentDate<=')
        .concat(values.endDate.toString());
    }
    if (values?.appointmentType) {
      if (filterString !== '') {
        filterString = filterString.concat('&');
      }
      const appointmentTypeFltr = values.appointmentType
        .map((type: OptionType) => type.id)
        .join(',');
      filterString = filterString
        .concat('filterByAppointmentTypes=')
        .concat(appointmentTypeFltr);
    }
    if (values?.clientIds) {
      if (filterString !== '') {
        filterString = filterString.concat('&');
      }
      const clientFltr = values.clientIds
        .map((client) => client.clientId)
        .join(',');
      filterString = filterString
        .concat('filterByClientIds=')
        .concat(clientFltr);
    }
    if (values?.attendeeIds) {
      if (filterString !== '') {
        filterString = filterString.concat('&');
      }
      const attendeeFltr = values.attendeeIds
        .map((attendee) => attendee.id)
        .join(',');
      filterString = filterString
        .concat('filterByAttendeeIds=')
        .concat(attendeeFltr);
    }

    const encodedFltr = encodeURI(filterString);
    setPage(0);
    setFilterData(encodedFltr);
  };

  // Memoize rawData to avoid recalculating it on every render
  const rawData: RawData[] = useMemo(() => {
    if (reportData.cancellationReportAppointments.length === 0) return [];
    return reportData.cancellationReportAppointments.map(
      (appointment: CancelationReportAppointments) => ({
        clientName: formatName(
          appointment.clientFirstName,
          appointment.clientMiddleName,
          appointment.clientLastName,
          appointment.clientSuffix,
        ),
        date: dayjs(
          new Date(appointment.appointmentStartTime + '.000+00:00'),
        ).format('MM/DD/YYYY'),
        appointmentType: appointment.appointmentType,
        attendee: appointment.attendeeFirstName
          ? `${appointment.attendeeLastName}, ${
              appointment.attendeeFirstName || ''
            }`.trim()
          : '-',
        canceledType: appointment.cancelReason?.split(' - ')[0] ?? '-',
        canceledReason: appointment.cancelReason?.split(' - ')[1]
          ? (appointment.cancelReason?.split(' - ')[1] || '-')
              .charAt(0)
              .toUpperCase() +
            (appointment.cancelReason?.split(' - ')[1] || '-').slice(1)
          : appointment.notes || '-',
        progressNotesStatus: appointment.progressNotesStatus.includes(
          'Canceled',
        )
          ? "<span style='color: red;'>Canceled</span>"
          : appointment.progressNotesStatus,
        dphId:
          appointment.dphId !== null && appointment.dphId !== ''
            ? appointment.dphId
            : '-',
      }),
    );
  }, [reportData.cancellationReportAppointments]);

  // Memoize tableData derived from rawData
  const tableData: RawData[] = useMemo(() => {
    return rawData.map((item) => ({
      ...item,
      appointmentType: item.appointmentType
        ? intl.formatMessage({
            id: item.appointmentType,
            defaultMessage: item.appointmentType,
          })
        : '-',
    }));
  }, [rawData]);

  const options = {
    layout: 'fitDataFill', // Ensures columns fit within the available space
    autoResize: true,
    movableColumns: false,
    resizableColumns: false,
    headerSort: false,
    groupBy: 'date',
    groupStartOpen: true,
    columnDefaults: {
      resizable: false, // Prevent resizing of columns
      tooltip: true, // Show full content on hover
      widthGrow: 1, // Allow columns to grow proportionally
      clipContents: true, // Clip content that overflows
    },

    groupHeader: (value: string, count: number) =>
      `Date: ${value} (${count} visit${count > 1 ? 's' : ''})`,
  };

  function compareObjectsDeeply(
    obj1: CancelationReportFilterType,
    obj2: CancelationReportFilterType,
  ): boolean {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  }

  const validateForm = (value: CancelationReportFilterType) => {
    const errors: Partial<CancelationReportFilterType> = {};
    if (
      value.startDate &&
      value.endDate &&
      dayjs(value.startDate).isAfter(dayjs(value.endDate))
    ) {
      errors.endDate = 'endDateGreaterThanStartDate';
    }
    if (datePickerMinDateMaxDateValidate(value.startDate)) {
      errors.startDate = 'datePickerMinDateMaxDateValidate';
    }
    if (datePickerMinDateMaxDateValidate(value.endDate)) {
      errors.endDate = 'datePickerMinDateMaxDateValidate';
    }

    return errors;
  };

  const handleDownload = () => {
    toggleLoader(true);
    downloadCancelationReport(filterData)
      .then(async (response) => {
        const blob = new Blob([response]);

        // Create a Blob URL
        const blobUrl = URL.createObjectURL(blob);

        // Open the Blob URL in a new tab
        const anchor = document.createElement('a');
        anchor.href = blobUrl;
        anchor.download = 'CancellationReport.xlsx'; // Set desired file name here

        // Append anchor to document body
        document.body.appendChild(anchor);

        // Trigger anchor click
        anchor.click();

        // Clean up after use
        URL.revokeObjectURL(blobUrl);
        document.body.removeChild(anchor);

        toggleLoader(false);
      })
      .catch((error) => {
        console.log(error);

        toggleLoader(false);
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('ConsentForms.downloadErrorMsg');
          setToastrDefaultMessage('Failed to download');
        }
      });
  };

  return (
    <>
      {toastrId && (
        <SnackBarComponent
          open={toastrId !== undefined}
          handleClose={() => {
            setToastrId(undefined);
          }}
          labelId={toastrId}
          defaultMessageId={toastrDefaultMessage}
          successOrError={successOrError}
        />
      )}
      <Box component="main">
        <Box component="section">
          <Box component="div" className="rowContainer">
            <Grid container>
              <Grid item xs={6} lg={6}>
                <Box className="flex__ align__items__center">
                  <TitleText
                    labelid="cancelationReportTitle"
                    defaultlabel="Cancellation Report"
                  />
                </Box>
              </Grid>

              <Grid item xs={6} lg={6} sx={{ textAlign: 'right' }}>
                <Box
                  sx={{
                    flexGrow: 0,
                  }}
                >
                  <Button
                    className="btn-primary btn-filter"
                    variant="outlined"
                    onClick={handlefilter}
                    endIcon={<KeyboardArrowDownIcon />}
                    startIcon={<Filtericon />}
                  >
                    <FormattedMessage id="FiterText" defaultMessage="Filter" />
                  </Button>
                  <MemorizedCustomMenu
                    id="demo-customized-menu"
                    anchorEl={anchorEl}
                    open={openit}
                    onClose={handlefilterClose}
                  >
                    <Formik
                      innerRef={formRef}
                      initialValues={filterPlainData}
                      validationSchema={validationSchema}
                      onSubmit={(values) => {
                        setAnchorEl(null);
                        applyFilter(values);
                      }}
                      validate={validateForm}
                    >
                      {({
                        setFieldValue,
                        handleSubmit,
                        errors,
                        touched,
                        values,
                      }) => (
                        <Grid container rowSpacing={2} columnSpacing={2}>
                          <Grid item xs={5.5}>
                            <DatePickerComponent
                              name="startDate"
                              value={
                                values.startDate
                                  ? dayjs(values.startDate)
                                  : null
                              }
                              labelid="startDateText"
                              defaultlabelid="Start Date"
                              handlechange={(date: Dayjs | null) => {
                                const formattedDate =
                                  dayjs(date).format('MM/DD/YYYY');
                                if (date === null) {
                                  setFieldValue('startDate', null);
                                } else {
                                  setFieldValue('startDate', formattedDate);
                                }
                                setFieldValue('endDate', null);
                              }}
                            />
                            {errors.startDate && touched.startDate && (
                              <MediumTypography
                                labelid={errors.startDate}
                                textColor="red"
                                marginTop="8px"
                              />
                            )}
                          </Grid>
                          <Grid item xs={1}></Grid>
                          <Grid item xs={5.5}>
                            <DatePickerComponent
                              name="endDate"
                              value={
                                values.endDate ? dayjs(values.endDate) : null
                              }
                              labelid="endDateText"
                              defaultlabelid="End Date"
                              handlechange={(date: Dayjs | null) => {
                                const formattedDate =
                                  dayjs(date).format('MM/DD/YYYY');
                                if (date === null) {
                                  setFieldValue('endDate', null);
                                } else {
                                  setFieldValue('endDate', formattedDate);
                                }
                              }}
                            />
                            {errors.endDate && touched.endDate && (
                              <MediumTypography
                                labelid={errors.endDate}
                                textColor="red"
                                marginTop="8px"
                              />
                            )}
                          </Grid>

                          <Grid item xs={12} className="multisearchHgt">
                            <MultipleAutocompleteSearch
                              labelId="scheduler.filter.appointmentsType"
                              defaultId="Select Appointments Type"
                              data={appointmentTypesFilter.filter(
                                (item) =>
                                  !values.appointmentType.some(
                                    (selectedItem) =>
                                      selectedItem.id === item.id,
                                  ),
                              )}
                              type="string"
                              onSelectItem={(value: OptionType[] | null) => {
                                setFieldValue('appointmentType', value ?? []);
                              }}
                              disableLabel={true}
                              optionHintId={'No Appointment Type Found'}
                              sxProps={{
                                [`& .${autocompleteClasses.popupIndicator}`]: {
                                  transform: 'none',
                                  marginTop: '2px',
                                },
                                backgroundColor: '#fff',
                              }}
                              value={values.appointmentType}
                            />
                          </Grid>

                          <Grid item xs={12} className="multisearchHgt">
                            <MultiSelectAutocompleteSearch
                              labelId="formBuilder.SelectClient"
                              defaultId="Select Client"
                              onlyName={true}
                              value={values.clientIds}
                              onTextChange={(
                                event: ChangeEvent<HTMLInputElement>,
                              ) => {
                                setClientSearchText(event.target.value.trim());
                                handleClientSearchChange(
                                  event.target.value.trim(),
                                );
                              }}
                              onSelectItem={(
                                value: MultiSelectOptioinType[],
                              ) => {
                                setClientSearchText('');
                                if (value.length > 0) {
                                  setFieldValue('clientIds', value);
                                } else {
                                  setFieldValue('clientIds', []);
                                }
                              }}
                              data={clients}
                              optionHintId={
                                'AddNewSchedulerEvent.noClientsFoundInOptions'
                              }
                            />
                          </Grid>

                          <Grid item xs={12} className="multisearchHgt">
                            <MultiSelectAutocompleteSearch
                              labelId="selectAttendeesText"
                              defaultId="Select Attendees"
                              onlyName={false}
                              onSelectItem={(
                                value: MultiSelectOptioinType[],
                              ) => {
                                if (value) {
                                  if (employeeList.length > 0) {
                                    setFieldValue('attendeeIds', value);
                                  } else {
                                    setFieldValue('attendeeIds', []);
                                  }
                                }
                              }}
                              value={
                                values.attendeeIds ? values.attendeeIds : []
                              }
                              highlightSelectedValues={[]}
                              highlightSelectedValuesTooltipLabelId=""
                              data={employeeList}
                              hideOption={null}
                              optionHintId={
                                'AddNewSchedulerEvent.noCliniciansFoundInOptions'
                              }
                            />
                          </Grid>

                          <Grid item xs={12} textAlign={right} mt={1.5}>
                            <Box
                              sx={{
                                justifyContent: 'right',
                                display: 'flex',
                              }}
                            >
                              <Box className="mr-md">
                                <ButtonComponent
                                  className="btn-primary btn-cancel btn_width_90px"
                                  variantType="contained"
                                  labelId="Dashboard.filtercancel"
                                  defaultLabelId="Cancel"
                                  onClick={() => {
                                    setAnchorEl(null);
                                  }}
                                  disabled={false}
                                />
                              </Box>
                              <Box>
                                <ButtonComponent
                                  className="btn-primary btn-submit btn_width_90px"
                                  variantType="contained"
                                  type="submit"
                                  labelId="Dashboard.filtersubmit"
                                  defaultLabelId="Apply"
                                  onClick={() => {
                                    if (
                                      compareObjectsDeeply(
                                        filterPlainData,
                                        values,
                                      )
                                    ) {
                                      return;
                                    }

                                    handleSubmit();
                                  }}
                                />
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      )}
                    </Formik>
                  </MemorizedCustomMenu>

                  {!offlineMode && (
                    <ButtonComponent
                      className="btn-primary btn-submit ml-sm"
                      labelId="ConsentForms.listActionDownloadToolTipLabel"
                      defaultLabelId="Download"
                      variantType="contained"
                      onClick={handleDownload}
                    />
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} lg={12}>
                {filterBy.length > 0 && (
                  <Box sx={{ marginBottom: '10px' }}>
                    <DisplayFilter
                      filteredData={filterBy}
                      handleCloseClick={(
                        index: number,
                        key: string | undefined,
                      ) => {
                        if (key === 'AddNewSchedulerEvent.appointmentType') {
                          const updatedFilterPlainData: CancelationReportFilterType =
                            {
                              ...filterPlainData,
                              appointmentType: [],
                            };
                          applyFilter(updatedFilterPlainData);
                        } else if (key === 'TaskManager.clientfilter') {
                          const updatedFilterPlainData: CancelationReportFilterType =
                            {
                              ...filterPlainData,
                              clientIds: [],
                            };
                          applyFilter(updatedFilterPlainData);
                        } else if (key === 'ViewSchedulerEvent.clinicians') {
                          const updatedFilterPlainData: CancelationReportFilterType =
                            {
                              ...filterPlainData,
                              attendeeIds: [],
                            };
                          applyFilter(updatedFilterPlainData);
                        } else if (key === 'dateText') {
                          const updatedFilterPlainData: CancelationReportFilterType =
                            {
                              ...filterPlainData,
                              startDate: null,
                              endDate: null,
                            };
                          applyFilter(updatedFilterPlainData);
                        }
                      }}
                      handleCloseClickArrayType={(
                        main: number,
                        sub: number,
                      ) => {
                        if (
                          filterBy[main].key === 'TaskManager.clientfilter' &&
                          Array.isArray(filterBy[main].value)
                        ) {
                          const updatedFilterPlainData: CancelationReportFilterType =
                            {
                              ...filterPlainData,
                              clientIds: filterPlainData.clientIds.filter(
                                (item, index) => index !== sub,
                              ),
                            };
                          applyFilter(updatedFilterPlainData);
                        } else if (
                          filterBy[main].key === 'ViewSchedulerEvent.clinicians'
                        ) {
                          const updatedFilterPlainData: CancelationReportFilterType =
                            {
                              ...filterPlainData,
                              attendeeIds: filterPlainData.attendeeIds.filter(
                                (item, index) => index !== sub,
                              ),
                            };
                          applyFilter(updatedFilterPlainData);
                        } else if (
                          filterBy[main].key ===
                          'AddNewSchedulerEvent.appointmentType'
                        ) {
                          const updatedFilterPlainData: CancelationReportFilterType =
                            {
                              ...filterPlainData,
                              appointmentType:
                                filterPlainData.appointmentType.filter(
                                  (item, index) => index !== sub,
                                ),
                            };
                          applyFilter(updatedFilterPlainData);
                        }
                      }}
                      handleClearClick={() => {
                        clearForm();
                      }}
                      sxProps={{ marginTop: '0px' }}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
          {reportData.totalCount === 0 && (
            <Box className="pb-md">
              <EmptyScreen
                titleLabelId="noCancelationReportTitle"
                defaultTitleText="No Cancellation Report yet."
                showButton={false}
              />
            </Box>
          )}
          {reportData.cancellationReportAppointments.length > 0 && (
            <>
              <Box
                style={{
                  display: 'grid',
                }}
              >
                <ReactTabulator
                  columns={columns}
                  data={tableData}
                  options={options}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
                className="pb-md"
              >
                <AppPagination
                  pageNumber={page}
                  paginationCount={reportData.totalCount}
                  handleChangePage={(_event, newPage) => {
                    if (newPage - 1 !== page) {
                      setPage(newPage - 1);
                    }
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default CancelationReport;
